// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — AchievementBadgeBook  (THE DEED SIGIL BADGE BOOK)
// ────────────────────────────────────────────────────────────────────
// The showcase. Every deed you've ever earned — in ANY IE game — rendered
// as a glowing sigil badge in one gilded book. This is the "show off
// everything you've earned" surface: foils live in the Foil Vault, cards
// in the Collection, and now DEEDS live here, each wearing its own mark.
//
// It is the badge-book twin of AchievementGallery, upgraded three ways:
//
//   1. EVERY achievement is a SIGIL BADGE — a struck medallion, not a row.
//      Earned badges are lit with a rarity-tinted rim + radiance (mythic
//      and legendary FLARE; common sits quiet). Locked ones are dim
//      embossed slots. Hidden ones are sealed "???" discs. The sigil
//      language matches SourceSigil (same radiance ladder, same <head>
//      keyframe discipline) so a deed badge and a card's provenance mark
//      feel forged in the same workshop.
//
//   2. TWO CHAPTERS, exactly as the universe is shaped:
//        • "Achievements — Terror"     (everything game:"terror")
//        • "Unstable Achievements"     (Pier + Snake Eyes + Sol + every
//                                        future title + empire meta —
//                                        i.e. every OTHER game, folded)
//      This is the Codex ↔ Pierre bridge made visible: Terror's own deeds
//      on one shelf, the rest of the Empire on the other. When Pierre ships
//      its trophy room, it renders THIS component with viewingSelf=false
//      against another player's uid — zero new plumbing.
//
//   3. OWNER vs VISITOR mode:
//        • viewingSelf=true  → your book. Renown, completion bar, locked
//                              slots visible (something to chase), and the
//                              Cross-Game Rewards rail (the interconnection).
//        • viewingSelf=false → someone else's book, reached by clicking a
//                              player. A clean flex-wall of ONLY what they've
//                              earned — the pure flex. No locked clutter, no
//                              reward gates. Just "look what I've done."
//
// ── SAFETY / LAWS ────────────────────────────────────────────────────
//   • READ-ONLY. Touches no write path. Reads solely via useAchievements(uid),
//     which live-reads profiles/{uid}/achievements — canonical & shared.
//   • Registry-driven. Add an achievement anywhere in achievements.js and a
//     badge appears here automatically, correctly chaptered, for free.
//   • Any uid works — your own or another player's — because the hook and the
//     shared profile node are cross-game by design.
//   • GM (Gilded Obsidian) palette only — this is UI chrome. No SD tokens.
//
// ── USAGE ────────────────────────────────────────────────────────────
//   // Inside Codex's "Deeds & Renown" tab (your own book):
//   <AchievementBadgeBook uid={profile.uid} viewingSelf onClose={onBack} />
//
//   // From Pierre / a Plaza player-inspect (someone else's book):
//   <AchievementBadgeBook
//     uid={targetUid}
//     viewingSelf={false}
//     playerName={targetName}
//     onClose={closeInspect}
//   />
// ════════════════════════════════════════════════════════════════════

import React, { useMemo } from "react";
import { GM, MANUSCRIPT_BG } from "./theme";
import { useAchievements } from "./useAchievements";
import {
  ACHIEVEMENTS,
  GAME_NAMESPACES,
  REWARD_GATES,
  ACHIEVEMENT_RARITY_META,
  getAchievementDisplay,
  missingForReward,
  allAchievementIds,
} from "./achievements";

// ── THE TWO CHAPTERS ──────────────────────────────────────────────────
// Terror stands alone. Everything else — Pier, Snake Eyes, Sol, and the
// empire meta capstone — is "Unstable" (the rest of the Empire, the part
// that lives out past Terror toward Pierre). Future games slot in here with
// zero code: any namespace that isn't "terror" folds into UNSTABLE_GAMES.
const TERROR_GAMES = ["terror"];
const UNSTABLE_GAMES = Object.keys(GAME_NAMESPACES).filter(
  (g) => !TERROR_GAMES.includes(g)
);

// Order within the Unstable chapter (empire meta last — it's the capstone).
const UNSTABLE_ORDER = ["pier", "casino", "sol", "empire"].filter((g) =>
  UNSTABLE_GAMES.includes(g)
);
// Any brand-new namespace not yet placed above still shows, appended in
// registry order, so a new game is never silently dropped.
const UNSTABLE_FULL_ORDER = [
  ...UNSTABLE_ORDER,
  ...UNSTABLE_GAMES.filter((g) => !UNSTABLE_ORDER.includes(g)),
];

// ════════════════════════════════════════════════════════════════════
// KEYFRAMES — same lifecycle-proof <head> injection SourceSigil uses.
// Namespaced `ab-` so it can never collide with `ss-` or `hf-`.
// ════════════════════════════════════════════════════════════════════
const AB_KEYFRAMES = `
@keyframes ab-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes ab-spin-rev { 0% { transform: rotate(360deg); } 100% { transform: rotate(0deg); } }
@keyframes ab-pulse {
  0%,100% { opacity: 0.40; transform: scale(1); }
  50%     { opacity: 0.85; transform: scale(1.10); }
}
@keyframes ab-glint {
  0%   { transform: translateX(-150%) skewX(-18deg); opacity: 0; }
  22%  { opacity: 0.85; }
  55%  { transform: translateX(150%)  skewX(-18deg); opacity: 0; }
  100% { transform: translateX(150%)  skewX(-18deg); opacity: 0; }
}
@keyframes ab-bar {
  from { width: 0%; }
}
@media (prefers-reduced-motion: reduce) {
  .ab-halo, .ab-glow, .ab-glint { animation: none !important; }
}
`;
const AB_STYLE_ID = "ab-keyframes";
function ensureBadgeKeyframes() {
  if (typeof document === "undefined") return;      // SSR-safe
  if (document.getElementById(AB_STYLE_ID)) return; // already injected
  const el = document.createElement("style");
  el.id = AB_STYLE_ID;
  el.textContent = AB_KEYFRAMES;
  document.head.appendChild(el);
}
ensureBadgeKeyframes();

// ── Radiance ladder mapped from rarity ────────────────────────────────
// Rarer deeds announce themselves louder, mirroring SourceSigil's 0..3.
//   common/uncommon → 1 (quiet glow)
//   rare/epic       → 2 (glow + rotating halo)
//   legendary/mythic→ 3 (glow + halo + sweeping glint)
function radianceForRarity(rarity) {
  const order = ACHIEVEMENT_RARITY_META[rarity]?.order ?? 0;
  if (order >= 4) return 3; // legendary, mythic
  if (order >= 2) return 2; // rare, epic
  return 1;                 // common, uncommon
}

// ════════════════════════════════════════════════════════════════════
// DeedBadge — one struck medallion.
// Layers, back to front: rotating halo → breathing glow → medallion disc
// (rarity-rimmed) → glyph/emoji → sweeping glint. Locked = dim embossed
// slot; hidden+unearned = sealed "???" disc.
// ════════════════════════════════════════════════════════════════════
function DeedBadge({ id, record, tint, size = 96 }) {
  const def = ACHIEVEMENTS[id];
  const earned = !!record;
  const display = getAchievementDisplay(id, earned);
  const rarity =
    ACHIEVEMENT_RARITY_META[def.rarity] || ACHIEVEMENT_RARITY_META.common;
  const r = earned ? radianceForRarity(def.rarity) : 0;

  const showHalo = r >= 2;
  const showGlow = r >= 1;
  const showGlint = r >= 3;

  const rim = earned ? rarity.tint : GM.pageBorder;
  const face = earned
    ? `radial-gradient(circle at 38% 30%, ${GM.pageLight}, ${GM.pageMid} 60%, ${GM.page})`
    : GM.pageMid;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: size + 44,
        textAlign: "center",
      }}
      title={
        display.masked
          ? "A secret deed, yet to be discovered."
          : `${display.name} — ${display.desc}`
      }
    >
      {/* ── The medallion ──────────────────────────────────── */}
      <div
        style={{
          position: "relative",
          width: size,
          height: size,
          flexShrink: 0,
          overflow: "hidden",
          filter: earned ? "none" : "grayscale(0.9)",
          opacity: earned ? 1 : 0.5,
        }}
      >
        {/* rotating halo (radiance ≥ 2) */}
        {showHalo && (
          <div
            className="ab-halo"
            style={{
              position: "absolute",
              inset: "-12%",
              borderRadius: "50%",
              background: `conic-gradient(from 0deg, transparent 0deg, ${tint}55 70deg, transparent 150deg, ${rarity.tint}55 230deg, transparent 320deg)`,
              animation: `ab-spin ${r >= 3 ? 7 : 9}s linear infinite`,
              filter: "blur(2px)",
              opacity: 0.75,
            }}
          />
        )}
        {/* breathing glow (radiance ≥ 1) */}
        {showGlow && (
          <div
            className="ab-glow"
            style={{
              position: "absolute",
              inset: "6%",
              borderRadius: "50%",
              background: `radial-gradient(circle, ${tint}55 0%, ${tint}1f 46%, transparent 70%)`,
              animation: "ab-pulse 3s ease-in-out infinite",
            }}
          />
        )}

        {/* medallion disc */}
        <div
          style={{
            position: "absolute",
            inset: "10%",
            borderRadius: "50%",
            background: face,
            border: `2px solid ${rim}`,
            boxShadow: earned
              ? `0 0 14px ${tint}44, inset 0 2px 6px rgba(0,0,0,0.5), inset 0 0 0 3px ${GM.page}`
              : `inset 0 2px 6px rgba(0,0,0,0.55), inset 0 0 0 3px ${GM.page}`,
            display: "grid",
            placeItems: "center",
          }}
        >
          {/* inner rarity ring for earned badges — a struck bezel */}
          {earned && (
            <div
              style={{
                position: "absolute",
                inset: "12%",
                borderRadius: "50%",
                border: `1px solid ${rarity.tint}66`,
                boxShadow: `0 0 6px ${rarity.tint}33 inset`,
                pointerEvents: "none",
              }}
            />
          )}
          <span
            style={{
              fontSize: Math.round(size * 0.34),
              lineHeight: 1,
              filter: earned
                ? `drop-shadow(0 0 6px ${tint}aa)`
                : "none",
              opacity: earned ? 1 : 0.7,
            }}
          >
            {earned ? display.icon : display.masked ? "✶" : "🔒"}
          </span>
        </div>

        {/* sweeping glint (radiance 3 only) */}
        {showGlint && (
          <div
            className="ab-glint"
            style={{
              position: "absolute",
              inset: "10%",
              borderRadius: "50%",
              background:
                "linear-gradient(105deg, transparent 42%, rgba(255,255,255,0.5) 50%, transparent 58%)",
              mixBlendMode: "screen",
              animation: "ab-glint 3.8s ease-in-out infinite",
              pointerEvents: "none",
            }}
          />
        )}
      </div>

      {/* ── Rarity ribbon ──────────────────────────────────── */}
      <div
        style={{
          fontSize: 8,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontWeight: 700,
          color: earned ? rarity.tint : GM.vellumFaint,
          marginTop: 8,
        }}
      >
        {display.masked ? "Sealed" : rarity.label}
        {def.points && earned ? (
          <span style={{ color: GM.vellumFaint }}> · {def.points}</span>
        ) : null}
      </div>

      {/* ── Name ───────────────────────────────────────────── */}
      <div
        style={{
          fontFamily: GM.fontD,
          fontSize: 12.5,
          fontWeight: 700,
          lineHeight: 1.2,
          color: earned ? GM.vellum : GM.vellumFaint,
          marginTop: 2,
          minHeight: 30,
          maxWidth: size + 40,
        }}
      >
        {display.name}
      </div>

      {/* ── Earned date (owner sees provenance) ────────────── */}
      {earned && record?.earnedAt ? (
        <div style={{ fontSize: 8.5, color: GM.vellumFaint, marginTop: 3 }}>
          {new Date(record.earnedAt).toLocaleDateString()}
        </div>
      ) : null}
    </div>
  );
}

// ── A shelf of badges under a game/section heading ────────────────────
function BadgeShelf({ title, tint, ids, map, earnedOnly }) {
  // In visitor mode we only show earned badges; owner mode shows all.
  const shown = earnedOnly ? ids.filter((id) => map[id]) : ids;
  if (shown.length === 0) return null;

  const earnedHere = ids.filter((id) => map[id]).length;

  return (
    <section style={{ marginBottom: 26 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: tint,
            boxShadow: `0 0 8px ${tint}aa`,
          }}
        />
        <h4
          style={{
            fontFamily: GM.fontD,
            fontSize: 15,
            margin: 0,
            color: GM.vellum,
            letterSpacing: "0.05em",
          }}
        >
          {title}
        </h4>
        <span style={{ fontSize: 11, color: GM.vellumFaint }}>
          {earnedOnly ? shown.length : `${earnedHere}/${ids.length}`}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 14,
          justifyContent: "flex-start",
        }}
      >
        {shown.map((id) => (
          <DeedBadge
            key={id}
            id={id}
            record={map[id]}
            tint={tint}
          />
        ))}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════
// A CHAPTER — a titled group of shelves inside a framed panel.
//   "Achievements — Terror"      : the Terror shelf(s)
//   "Unstable Achievements"      : Pier / Casino / Sol / Empire shelves
// ════════════════════════════════════════════════════════════════════
function Chapter({ heading, subtitle, gameKeys, map, earnedOnly }) {
  // Bucket + sort each game's ids: earned first, then rarest first.
  const shelves = useMemo(() => {
    const out = [];
    for (const g of gameKeys) {
      const ns = GAME_NAMESPACES[g];
      if (!ns) continue;
      const ids = allAchievementIds().filter((id) => ACHIEVEMENTS[id].game === g);
      ids.sort((a, b) => {
        const ea = !!map[a], eb = !!map[b];
        if (ea !== eb) return ea ? -1 : 1;
        const ra = ACHIEVEMENT_RARITY_META[ACHIEVEMENTS[a].rarity]?.order ?? 0;
        const rb = ACHIEVEMENT_RARITY_META[ACHIEVEMENTS[b].rarity]?.order ?? 0;
        return rb - ra;
      });
      out.push({ g, label: ns.label, tint: ns.tint, ids });
    }
    return out;
  }, [gameKeys, map]);

  // In visitor mode, if the whole chapter is empty, show a soft placeholder
  // rather than a blank void — the flex should still read as "a book."
  const anyEarned = shelves.some((s) => s.ids.some((id) => map[id]));
  const showPlaceholder = earnedOnly && !anyEarned;

  const totalEarned = shelves.reduce(
    (n, s) => n + s.ids.filter((id) => map[id]).length,
    0
  );
  const totalAll = shelves.reduce((n, s) => n + s.ids.length, 0);

  return (
    <div
      style={{
        position: "relative",
        borderRadius: 14,
        border: `1px solid ${GM.pageBorder}`,
        background: `linear-gradient(180deg, ${GM.pageWarm}, ${GM.page})`,
        boxShadow: `inset 0 1px 0 ${GM.inkGold}14, 0 8px 24px rgba(0,0,0,0.4)`,
        padding: "20px 20px 8px",
        marginBottom: 26,
      }}
    >
      {/* Chapter header */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 4,
        }}
      >
        <h3
          style={{
            fontFamily: GM.fontD,
            fontSize: 19,
            margin: 0,
            color: GM.inkGoldBright,
            letterSpacing: "0.05em",
          }}
        >
          {heading}
        </h3>
        <span style={{ fontSize: 11, color: GM.vellumFaint, whiteSpace: "nowrap" }}>
          {earnedOnly ? `${totalEarned} earned` : `${totalEarned}/${totalAll}`}
        </span>
      </div>
      {subtitle ? (
        <div style={{ fontSize: 11, color: GM.vellumDim, marginBottom: 18 }}>
          {subtitle}
        </div>
      ) : (
        <div style={{ height: 12 }} />
      )}

      {showPlaceholder ? (
        <div
          style={{
            padding: "22px 10px 30px",
            textAlign: "center",
            color: GM.vellumFaint,
            fontSize: 12.5,
            fontStyle: "italic",
          }}
        >
          No deeds claimed here yet — this shelf awaits its first sigil.
        </div>
      ) : (
        shelves.map((s) => (
          <BadgeShelf
            key={s.g}
            title={s.label}
            tint={s.tint}
            ids={s.ids}
            map={map}
            earnedOnly={earnedOnly}
          />
        ))
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Cross-Game Rewards rail — the interconnection made visible (owner only).
// Surfaces REWARD_GATES: which deed (often from another game) unlocks which
// reward. Hidden-only gates are omitted (discovery only).
// ════════════════════════════════════════════════════════════════════
function CrossGameRewards({ map }) {
  const gates = useMemo(
    () =>
      Object.entries(REWARD_GATES).filter(([, gate]) => {
        const reqs = gate.requires || gate.anyOf || [];
        return !reqs.every((id) => ACHIEVEMENTS[id]?.hidden);
      }),
    []
  );
  if (gates.length === 0) return null;

  return (
    <section style={{ marginBottom: 30 }}>
      <h3
        style={{
          fontFamily: GM.fontD,
          fontSize: 17,
          margin: "0 0 4px",
          color: GM.inkGoldBright,
          letterSpacing: "0.05em",
        }}
      >
        ⛓ Cross-Game Rewards
      </h3>
      <div style={{ fontSize: 11, color: GM.vellumDim, marginBottom: 12 }}>
        Deeds in one corner of the Empire open doors in another.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 12,
        }}
      >
        {gates.map(([rewardId, gate]) => {
          const missing = missingForReward(map, rewardId);
          const unlocked = missing.length === 0;
          const reqs = gate.requires || gate.anyOf || [];
          return (
            <div
              key={rewardId}
              style={{
                padding: "12px 14px",
                borderRadius: 9,
                background: unlocked
                  ? `linear-gradient(180deg, ${GM.pageMid}, ${GM.pageWarm})`
                  : GM.page,
                border: `1px solid ${unlocked ? GM.inkGold : GM.pageBorder}`,
                boxShadow: unlocked
                  ? `0 0 12px ${GM.inkGold}33, inset 0 1px 0 ${GM.inkGold}22`
                  : "none",
                opacity: unlocked ? 1 : 0.78,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 8,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: GM.vellumFaint,
                    fontWeight: 700,
                  }}
                >
                  {gate.type}
                </span>
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: unlocked ? GM.inkGoldBright : GM.vellumFaint,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                  }}
                >
                  {unlocked ? "✦ Unlocked" : "🔒 Locked"}
                </span>
              </div>
              <div
                style={{
                  fontFamily: GM.fontD,
                  fontSize: 14,
                  fontWeight: 700,
                  color: GM.vellum,
                  margin: "4px 0 6px",
                }}
              >
                {prettyRewardId(rewardId)}
              </div>
              <div style={{ fontSize: 11, color: GM.vellumDim, lineHeight: 1.35 }}>
                {gate.note}
              </div>
              {!unlocked && (
                <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 8 }}>
                  {gate.anyOf ? "Needs any of: " : "Needs: "}
                  {missing.map((id) => ACHIEVEMENTS[id]?.name || id).join(", ")}
                </div>
              )}
              {unlocked && reqs.length > 0 && (
                <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 8 }}>
                  Earned via:{" "}
                  {reqs.map((id) => ACHIEVEMENTS[id]?.name || id).join(", ")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// "radiant:jynxi_green" → "J'ynxi Green (Radiant)"
function prettyRewardId(rewardId) {
  const [type, key] = rewardId.split(":");
  const words = (key || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
  return `${words} (${typeLabel})`;
}

// ════════════════════════════════════════════════════════════════════
// AchievementBadgeBook — the whole book.
//   uid          which player's deeds to show (yours OR another's).
//   viewingSelf  true → owner view (renown, locked slots, reward gates).
//                false → visitor view (earned-only flex-wall, clean).
//   playerName   shown in the header for visitor view ("<name>'s Deeds").
//   onClose      back handler (optional).
// ════════════════════════════════════════════════════════════════════
export default function AchievementBadgeBook({
  uid,
  viewingSelf = true,
  playerName = "",
  onClose,
}) {
  ensureBadgeKeyframes();
  const { map, count, renown } = useAchievements(uid);

  const total = useMemo(() => allAchievementIds().length, []);
  const pct = total ? Math.round((count / total) * 100) : 0;
  const earnedOnly = !viewingSelf;

  const title = viewingSelf
    ? "The Deed Sigil Book"
    : `${playerName ? `${playerName}'s ` : ""}Deed Sigil Book`;

  const subline = viewingSelf
    ? `${count} of ${total} deeds claimed · ${pct}% · ${renown} renown`
    : `${count} deed${count === 1 ? "" : "s"} claimed · ${renown} renown`;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 800,
        overflowY: "auto",
        background: MANUSCRIPT_BG,
        fontFamily: GM.font,
        color: GM.vellum,
        padding: "24px 16px 64px",
      }}
    >
      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        {/* ── Header ─────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 16,
            borderBottom: `1px solid ${GM.pageBorder}`,
            paddingBottom: 14,
            marginBottom: 22,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: GM.fontD,
                fontSize: 27,
                fontWeight: 700,
                color: GM.inkGoldBright,
                letterSpacing: "0.04em",
              }}
            >
              ✦ {title}
            </div>
            <div style={{ fontSize: 12, color: GM.vellumDim, marginTop: 4 }}>
              {subline}
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={ghostBtn}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = GM.inkGoldBright)
              }
              onMouseLeave={(e) => (e.currentTarget.style.color = GM.vellumDim)}
            >
              ← Back
            </button>
          )}
        </div>

        {/* completion bar (owner only — a visitor sees no locked chase) */}
        {viewingSelf && (
          <div
            style={{
              height: 6,
              borderRadius: 4,
              background: GM.pageMid,
              border: `1px solid ${GM.pageBorder}`,
              overflow: "hidden",
              marginBottom: 26,
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: "100%",
                background: `linear-gradient(90deg, ${GM.inkGoldDim}, ${GM.inkGoldBright})`,
                animation: "ab-bar 700ms ease",
                transition: "width 600ms ease",
              }}
            />
          </div>
        )}

        {/* ── Chapter I — Terror ─────────────────────────────── */}
        <Chapter
          heading="Achievements — Terror"
          subtitle="Deeds struck in the Krush Universe."
          gameKeys={TERROR_GAMES}
          map={map}
          earnedOnly={earnedOnly}
        />

        {/* ── Chapter II — Unstable (every other IE game) ────── */}
        <Chapter
          heading="Unstable Achievements"
          subtitle="Deeds from across the Empire — the Pier, the Casino, and beyond."
          gameKeys={UNSTABLE_FULL_ORDER}
          map={map}
          earnedOnly={earnedOnly}
        />

        {/* ── Cross-Game Rewards rail (owner only) ───────────── */}
        {viewingSelf && <CrossGameRewards map={map} />}
      </div>
    </div>
  );
}

const ghostBtn = {
  background: "transparent",
  border: "none",
  color: GM.vellumDim,
  fontFamily: GM.font,
  fontSize: 13,
  letterSpacing: "0.06em",
  cursor: "pointer",
  padding: "6px 4px",
  transition: "color 160ms ease",
};
